import React, { useState, useEffect, useCallback } from 'react'
import dayjs from 'dayjs'
import { FieldTypes, userTypes } from '../../utils/constants'
import {
  durationOptions,
  cancellationPeriodUnitOptions,
  cancellationPeriodValuesOptions,
} from '../../utils/amenityConstants'
import Form from '../common/Form'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import ListHeader from '../common/ListHeader'
import useStyles from '../common/FormFields/FormFieldsStyle'
import useAmenitiesApi from '../../hooks/amenities/useAmenitiesApi'
import useBuildingsApi from '../../hooks/buildings/useBuildingsApi'
import cookies from '../../utils/cookies'

// DayTimePicker stores time as ISO string for backwards-compat with the
// Schedule feature; the backend amenity-schedule schema expects 'HH:mm'.
const toHHmm = (iso) => {
  if (!iso) return ''
  const d = dayjs(iso)
  if (!d.isValid()) return ''
  return d.format('HH:mm')
}

export default function AmenityForm({ onSubmit, handleCancel, amenity }) {
  const classes = useStyles()

  const [values, setValues] = useState(() => {
    const initialValues = {
      buildingId: '',
      amenityTypeId: '',
      name: '',
      minDurationMinutes: 60, // 1 hora por defecto
      maxDurationMinutes: 360, // 6 horas por defecto
      timeSlotMinutes: 60, // 1 hora por defecto
      maxSimultaneousReservationsPerUser: 1,
      pricePerSlot: 0,
      extraCostPerUse: 0,
      rulesText: '',
      cancellationPeriodValue: 24,
      cancellationPeriodUnit: 'hours',
      active: true,
      ...amenity,
    }
    return initialValues
  })

  // Horarios de disponibilidad
  const [schedules, setSchedules] = useState(amenity?.schedules || [{}])

  const [amenitiesApiState, amenityMethods] = useAmenitiesApi()
  const [buildingsApiState, buildingMethods] = useBuildingsApi()

  const { amenityTypesState } = amenitiesApiState
  const { buildingsState } = buildingsApiState

  useEffect(() => {
    amenityMethods.getAmenityTypes()
    buildingMethods.getBuildings(true)
  }, [])

  const updateScheduleData = useCallback((index, data) => {
    setSchedules((prevSchedules) => {
      const updatedSchedules = [...prevSchedules]
      updatedSchedules[index] = { ...updatedSchedules[index], ...data }
      return updatedSchedules
    })
  }, [])

  const removeSchedule = useCallback((index) => {
    setSchedules((prevSchedules) => prevSchedules.filter((_, i) => i !== index))
  }, [])

  const addSchedule = useCallback(() => {
    setSchedules((prevSchedules) => [...prevSchedules, {}])
  }, [])

  const timeSlotOptions = durationOptions.filter(
    (option) => option.value <= 480
  )

  const fields = [
    {
      type: FieldTypes.TextField,
      label: 'Nombre del Amenity',
      hint: 'Ingrese el nombre del amenity',
      key: 'name',
      errorMessage: 'El nombre es requerido',
      required: true,
    },
    {
      type: FieldTypes.Select,
      id: 'buildingId',
      label: 'Edificio/Barrio',
      hint: 'Seleccione un edificio o barrio',
      key: 'buildingId',
      errorMessage: 'El edificio es requerido',
      required: true,
      options: (buildingsState.buildings || []).map((building) => ({
        value: building.id,
        label: building.name,
      })),
      withMargin: true,
    },
    {
      type: FieldTypes.Select,
      id: 'amenityTypeId',
      label: 'Tipo de Amenity',
      hint: 'Seleccione el tipo de amenity',
      key: 'amenityTypeId',
      errorMessage: 'El tipo es requerido',
      required: true,
      options: (amenityTypesState.amenityTypes || []).map((type) => ({
        value: type.id,
        label: type.name,
      })),
      withMargin: true,
    },
    {
      type: FieldTypes.Select,
      id: 'minDurationMinutes',
      label: 'Duración Mínima de Reserva',
      hint: 'Seleccione la duración mínima',
      key: 'minDurationMinutes',
      errorMessage: 'La duración mínima es requerida',
      required: true,
      options: durationOptions,
      withMargin: true,
    },
    {
      type: FieldTypes.Select,
      id: 'maxDurationMinutes',
      label: 'Duración Máxima de Reserva',
      hint: 'Seleccione la duración máxima',
      key: 'maxDurationMinutes',
      errorMessage: 'La duración máxima es requerida',
      required: true,
      options: durationOptions,
      withMargin: true,
    },
    {
      type: FieldTypes.Select,
      id: 'timeSlotMinutes',
      label: 'Fracción de Tiempo',
      hint: 'Seleccione la fracción de tiempo para reservas',
      key: 'timeSlotMinutes',
      errorMessage: 'La fracción de tiempo es requerida',
      required: true,
      options: timeSlotOptions,
      withMargin: true,
    },
    {
      type: FieldTypes.TextField,
      label: 'Cantidad de Reservas Simultáneas por Usuario',
      hint: 'Número máximo de reservas simultáneas',
      key: 'maxSimultaneousReservationsPerUser',
      errorMessage: 'Este campo es requerido',
      required: true,
      inputType: 'number',
      withMargin: true,
    },
    {
      type: FieldTypes.TextField,
      label: 'Costo Fijo por Fracción',
      hint: 'Costo base por fracción de tiempo (0 = gratis)',
      key: 'pricePerSlot',
      inputType: 'number',
      step: '0.01',
      withMargin: true,
    },
    {
      type: FieldTypes.TextField,
      label: 'Costo Variable por Uso',
      hint: 'Costo adicional variable (ej: limpieza, mantenimiento)',
      key: 'extraCostPerUse',
      inputType: 'number',
      step: '0.01',
      withMargin: true,
    },
    {
      type: FieldTypes.Select,
      id: 'timeSlotMinutes',
      label: 'Valor de periodo de cancelación',
      key: 'cancellationPeriodValue',
      errorMessage: 'El valor de periodo de cancelacion es requerido',
      required: true,
      options: cancellationPeriodValuesOptions,
      withMargin: true,
    },
    {
      type: FieldTypes.Select,
      id: 'timeSlotMinutes',
      label: 'Unidad de periodo de cancelación',
      key: 'cancellationPeriodUnit',
      errorMessage: 'La unidad de periodo de cancelacion es requerida',
      required: true,
      options: cancellationPeriodUnitOptions,
      withMargin: true,
    },
    {
      type: FieldTypes.TextArea,
      label: 'Reglas de Uso',
      hint: 'Ingrese las normativas de uso del amenity (EULA)',
      key: 'rulesText',
      rows: 5,
      withMargin: true,
    },
    {
      type: FieldTypes.Switch,
      label: 'Amenity Activo',
      key: 'active',
    },
    {
      type: FieldTypes.Schedules,
      schedules: schedules,
      updateScheduleData: updateScheduleData,
      removeSchedule: removeSchedule,
      setSchedules: addSchedule,
    },
  ]

  return (
    <div className={classes.paper}>
      <ListHeader
        bigTitle={`${amenity && amenity.id ? 'Editar' : 'Crear'} Amenity`}
        canCreate={false}
      />
      <Form
        values={values}
        setValues={setValues}
        fields={fields}
        extraWidth
        handleSubmit={() => {
          // Cross-field validation lives on the backend yup schema; let the
          // server respond and the toast surface its message.

          // Transform the DayTimePicker output (days bitstring + ISO datetimes)
          // into the backend shape: one row per active day with HH:mm times.
          const expanded = (schedules || []).reduce((acc, s) => {
            if (!s || !s.days || !s.startTime || !s.endTime) return acc
            const startTime = toHHmm(s.startTime)
            const endTime = toHHmm(s.endTime)
            if (!startTime || !endTime) return acc
            const days = String(s.days).split('')
            days.forEach((bit, dayOfWeek) => {
              if (bit === '1') {
                acc.push({ dayOfWeek, startTime, endTime, active: true })
              }
            })
            return acc
          }, [])

          onSubmit({
            ...values,
            schedules: expanded,
          })
        }}
        handleCancel={handleCancel}
      />
    </div>
  )
}
